import { useCallback, useRef } from 'react';
import { api } from '../api/fetch';
import { logDataLayerEvent } from '../utils/tagManager';

const TRACK_ENDPOINT = '/user/event/track';

/** A `?bg=` value worth recording: an image name, nothing else. */
const BG_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * The current pathname plus the landing background (`?bg=`), e.g.
 * `/l/a?bg=babe-3`. Only `bg` is kept: lander URLs can carry an email
 * (`epb`) and other tracking params that must not reach BigQuery.
 * A value outside BG_PATTERN is dropped.
 */
export const currentLandingUrl = (): string => {
  const { pathname, search } = window.location;
  const bg = new URLSearchParams(search).get('bg');

  return bg !== null && BG_PATTERN.test(bg) ? `${pathname}?bg=${bg}` : pathname;
};

/**
 * Allowed event action names. Must match the whitelist in
 * common/modules/user/controllers/EventController.php
 */
type TrackingAction =
  | 'advertiser_page'
  | 'page_interact'
  | 'cta_click'
  | 'signup_form_interact'
  | 'signup_form_submit'
  | 'picture_upload_attempt'
  | 'picture_upload_processing_failed'
  | 'picture_upload_validation_failed'
  | 'picture_upload_request_failed';

interface TrackingData {
  landingUrl?: string;
}

/**
 * Lightweight visitor-event tracking hook.
 *
 * Sends events to the PHP endpoint which enriches them with session,
 * device, geo and campaign data before pushing to BigQuery via Redis.
 *
 * - Fire-and-forget: errors are silently swallowed (analytics should
 *   never break the user experience).
 * - Deduplication: the same (action + JSON data) combination is
 *   suppressed for 2 seconds to avoid accidental double-fires from
 *   React StrictMode re-renders or rapid user interactions.
 * - Landing page is auto-detected from the current pathname (plus a
 *   sanitised `?bg=`, see currentLandingUrl) but can be overridden in
 *   the data object.
 *
 * Usage:
 *   const track = useTracking();
 *   track('cta_click');
 *   track('signup_form_interact');
 */
export const useTracking = (): ((action: TrackingAction, data?: TrackingData) => void) => {
  // Track recently fired events to deduplicate
  const recentRef = useRef<Map<string, number>>(new Map());

  const track = useCallback((action: TrackingAction, data?: TrackingData) => {
    const payload: TrackingData & { action: string } = {
      action,
      landingUrl: currentLandingUrl(),
      ...data,
    };

    // Deduplication: skip if the exact same event was sent < 2s ago
    const key = `${action}:${JSON.stringify(data ?? {})}`;
    const now = Date.now();
    const lastSent = recentRef.current.get(key);
    if (lastSent && now - lastSent < 2000) {
      return;
    }
    recentRef.current.set(key, now);

    // Prune old entries periodically (keep map small)
    if (recentRef.current.size > 50) {
      for (const [k, ts] of recentRef.current) {
        if (now - ts > 5000) {
          recentRef.current.delete(k);
        }
      }
    }

    // Fire-and-forget — never let tracking break the UI
    logDataLayerEvent(action, { landingUrl: payload.landingUrl });
    api.post(TRACK_ENDPOINT, payload).catch(() => {});
  }, []);

  return track;
};
